import { Module } from 'lib/vendor/terrific/Module';

import { FallbackProviderConfig, fallbackProvider } from './providers/fallback';
import { SegmentMappingProviderConfig, segmentMappingProvider } from './providers/segmentMapping';
import { isEditMode, namespace } from 'lib/util/helpers';
import * as $ from 'jquery';

export interface Provider<C extends AbstractProviderConfig> {
	(config : C) : Promise<string | null>;
}

export interface AbstractProviderConfig {
	type : string;
}

type ProviderConfig = FallbackProviderConfig | SegmentMappingProviderConfig;

interface PlaceholderConfig {
	name : string;
	providers : ProviderConfig[];
}

export interface TemplatingContainerConfig {
	placeholders : PlaceholderConfig[];
	prefix : string;
}

export function configuredProvider(config : ProviderConfig) : (() => Promise<string | null>) | null {
	switch(config.type) {
		case 'fallback':
			return fallbackProvider.bind(null, config);
		case 'segmentMapping':
			return segmentMappingProvider.bind(null, config);
		default:
			console.warn('Templating provider for config', config, 'not registered');

			return null;
	}
}

async function resolve(placeholder : string, providers : ProviderConfig[]) : Promise<[string, string | null]> {
	for(const providerConfig of providers) {
		const provider = configuredProvider(providerConfig);
		if(!provider) {
			continue;
		}
		try {
			const result = await provider();
			if(typeof result === 'string') {
				return [placeholder, result];
			}
		} catch(e) {
			console.warn(`Error resolving placeholder ${placeholder} from`, providerConfig, e);
		}
	}

	return [placeholder, null];
}

export async function resolveAll(variables : PlaceholderConfig[]) {
	const replacements = await Promise.all(
		variables.map(c => resolve(c.name, c.providers)),
	);

	const resolved : ResolvedPlaceholders = {};
	for(const [name, rep] of replacements) {
		if(rep) {
			resolved[name] = rep;
		}
	}

	return resolved;
}

interface ResolvedPlaceholders {
	[placeholder : string] : string;
}

function escapeRegExp(re : string) {
	return re.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function transform(text : string, prefix : string, resolvedPlaceholders : ResolvedPlaceholders) {
	return text.replace(new RegExp(`${escapeRegExp(prefix)}\\{([^}]+)\\}`, 'g'), (_str, placeholder) => {
		if(placeholder in resolvedPlaceholders) {
			return resolvedPlaceholders[placeholder];
		}

		return '';
	});
}

/**
 * Render templating content of all templating components in preview
 */
export function refreshAllTemplatingContainers() {
	return Promise.all(
		$('.modTemplatingContainer')
			.get()
			.map(el => {
				return (window.application.sandbox.getModuleInstance(el, 'TemplatingContainer') as TemplatingContainer).refresh();
			}),
	);
}

const templating = namespace('SCS.templating');

export function processMBoxContent(mbox : HTMLElement) {
	const $mbox = $(mbox);
	$mbox.parents('.modTemplatingContainer').each((_index : any, $el : HTMLElement) => {
		(window.application.sandbox.getModuleInstance($el, 'TemplatingContainer') as TemplatingContainer)
			.processMBoxContent(mbox);
	});
}

if(!('processMBoxContent' in templating)) {
	templating.processMBoxContent = processMBoxContent;
}

export default class TemplatingContainer extends Module {
	private resolvedPlaceholders? : ResolvedPlaceholders;
	private template! : HTMLTemplateElement;
	private templatePrototype! : HTMLTemplateElement;
	private fragment? : DocumentFragment;
	private config! : TemplatingContainerConfig;

	public on(callback : () => void) {
		this.template = this.$ctx.find('> template')[0] as HTMLTemplateElement;
		if(isEditMode() || !this.template) {
			callback();

			return;
		}
		if ($(document.body).is('.activitypage')) {
			this.templatePrototype = this.template.cloneNode(true) as HTMLTemplateElement;
		}

		if(this.template.content) {
			this.fragment = this.template.content;
		} else {
			this.template.style.display = 'none';
		}
		this.config = this.$ctx.data('templatingConfiguration');

		this.process().catch(e => {
			console.error('Unable to transform template placeholders', e);
		});
		callback();
	}

	/**
	 * Render templating content in preview
	 */
	public async refresh() {
		this.template = this.templatePrototype.cloneNode(true) as HTMLTemplateElement;
		if(this.template.content) {
			this.fragment = this.template.content;
			// Remove previous content
			while(this.$ctx[0].hasChildNodes()) {
				this.$ctx[0].removeChild(this.$ctx[0].lastChild!);
			}
		} else {
			this.template.style.display = 'none';
			this.$ctx[0].innerHTML = '';
		}
		this.resolvedPlaceholders = undefined;
		try {
			await this.process();
		} catch(e) {
			console.error('Unable to transform template placeholders', e);
		}
	}

	public async process() {
		const templatingReadyEvent = 'TEMPLATING_READY';
		if(!this.resolvedPlaceholders) {
			this.resolvedPlaceholders = await resolveAll(this.config.placeholders);
		}

		this.log('Resolved placeholders', this.resolvedPlaceholders);

		if(!('XPathResult' in window) || !this.fragment) {
			// IE Does not support XPath, simply use innerHTML
			$(this.$ctx[0]).html(this.processStringContent(this.template.innerHTML));
			$(document.body).trigger(templatingReadyEvent);

			return;
		}

		if(!this.fragment.childNodes[0]) {

			return;
		}

		this.fragment = this.processContent(this.fragment);

		this.$ctx.append(this.fragment);
		$(document.body).trigger(templatingReadyEvent);
	}

	public processMBoxContent(mbox : HTMLElement) {
		mbox.innerHTML = this.processStringContent(mbox.innerHTML);
	}

	private processStringContent(content : string) : string {
		if(!this.resolvedPlaceholders) {
			return content;
		}

		return transform(content, this.config.prefix, this.resolvedPlaceholders);
	}

	private processContent(content : DocumentFragment) : DocumentFragment {
		if(!this.resolvedPlaceholders) {

			return content;
		}

		// Hack: You can’t use a document fragment as the context of an XPath query.
		//       But you can select it with `..` if you use its child node.
		const target = content.childNodes[0];

		if(!target) {
			return content;
		}

		const attrs = target.ownerDocument!.evaluate(
			`..//attribute::*[contains(., '${this.config.prefix}')]`,
			target,
			null,
			XPathResult.UNORDERED_NODE_SNAPSHOT_TYPE,
			null,
		);

		for (let i = 0; i < attrs.snapshotLength; i++) {
			const attr = attrs.snapshotItem(i) as Attr;
			this.log.debug('Replacing placeholders in attribute', attr);
			attr.value = transform(attr.value, this.config.prefix, this.resolvedPlaceholders);
		}

		const texts = target.ownerDocument!.evaluate(
			`..//text()[contains(., '${this.config.prefix}')]`,
			target,
			null,
			XPathResult.UNORDERED_NODE_SNAPSHOT_TYPE,
			null,
		);

		for (let i = 0; i < texts.snapshotLength; i++) {
			const text = texts.snapshotItem(i) as Text;
			const parent = text.parentNode!;
			this.log.debug('Replacing placeholders in text node', text);
			parent.replaceChild(document.createTextNode(transform(text.textContent!, this.config.prefix,
				this.resolvedPlaceholders)), text);
		}

		return content;
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/personalization/templating/Tc.Module.TemplatingContainer.ts